/* eslint-disable */

import React, { useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import PrimaryActionButton from '../sidepanel/PrimaryActionButton';
import FileCard from './FileCard';
import VideoDropBox from './VideoDropBox';
import { useVideoCreatorStore } from '../../stores-v2/VideoCreatorStoreContext';

import {
  gbToBytes,
  STORY_VIDEO_SIZE_GB,
  validateStoryVideo,
} from '../../services/upload/mediaValidation';

import ChevronDownIcon from '../../svgs/ChevronDownIcon';
import CloseIcon from '../../svgs/CloseIcon';
import DeleteIcon from '../../svgs/DeleteIcon';

interface FileWithIndex extends File {
  index: number;
}

interface MultiFileUploadConcatProps {
  handleStoryVideoUpload?: (files: File[]) => void;
  onFilesChange?: (files: File[]) => void;
  onClose?: () => void;
  maxUploadSizeGB?: number;
}

const formatSize = (bytes: number) =>
  bytes >= 1024 ** 3
    ? `${(bytes / 1024 ** 3).toFixed(2)} GB`
    : `${(bytes / 1024 ** 2).toFixed(1)} MB`;

export default observer((props: MultiFileUploadConcatProps) => {
  const {
    handleStoryVideoUpload,
    onFilesChange,
    onClose,
    maxUploadSizeGB = STORY_VIDEO_SIZE_GB.default,
  } = props;
  const [files, setFiles] = useState<FileWithIndex[]>([]);
  const videoCreator = useVideoCreatorStore();

  const maxSizeBytes = gbToBytes(maxUploadSizeGB);

  const handleFileSelect = useCallback(
    async (selectedFiles: File[]) => {
      if (!selectedFiles.length) return;

      // Deep-validate each file (size, true type, and later tiers). Rejected
      // files are toasted individually so the user knows which one failed.
      const checked = await Promise.all(
        selectedFiles.map(async file => ({
          file,
          result: await validateStoryVideo(file, { maxSizeBytes }),
        })),
      );

      const validFiles: File[] = [];
      checked.forEach(({ file, result }) => {
        if (result.ok) {
          validFiles.push(file);
        } else {
          videoCreator.showToast({
            state: 'error',
            message: `${file.name}: ${result.error.message}`,
          });
        }
      });

      const filesWithIndex = validFiles.map((file, index) =>
        Object.assign(file, { index: files.length + index }),
      );

      setFiles(prev => [...prev, ...filesWithIndex]);
    },
    [files.length, videoCreator, maxSizeBytes],
  );

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter(file => file.index !== index));
  };

  const moveFile = (currentIndex: number, direction: 'up' | 'down') => {
    setFiles(prev => {
      const fileIndex = prev.findIndex(f => f.index === currentIndex);
      if (fileIndex === -1) return prev;

      const newIndex = direction === 'up' ? fileIndex - 1 : fileIndex + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;

      const updated = [...prev];
      const temp = updated[fileIndex];
      updated[fileIndex] = updated[newIndex];
      updated[newIndex] = temp;

      return updated;
    });
  };

  // Notify parent component when files change
  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(files);
    }
    // Also call handleStoryVideoUpload for backward compatibility
    if (handleStoryVideoUpload && files.length > 0) {
      handleStoryVideoUpload(files);
    }
  }, [files, onFilesChange, handleStoryVideoUpload]);

  const handleSubmit = () => {
    if (files.length === 0) {
      videoCreator.showToast({
        state: 'error',
        message: 'Please select files to upload',
      });
      return;
    }

    // Pass the files to the parent component
    if (handleStoryVideoUpload) {
      handleStoryVideoUpload(files);
    }
  };

  return (
    <Container>
      {onClose && (
        <Header>
          <div />
          <CloseButton onClick={onClose} aria-label="Remove file">
            <CloseIcon strokeColor="var(--text-secondary)" />
          </CloseButton>
        </Header>
      )}

      {/* The same drop box as Single Video (PLAT-11941), in its multiple
          mode, so the two modes of Add Stories look and behave alike. */}
      <VideoDropBox
        multiple
        onFiles={handleFileSelect}
        showToast={toast => videoCreator.showToast(toast)}
        hint={`MP4 or MOV · up to ${maxUploadSizeGB} GB each`}
        inputTestId="story-video-concat-input"
      />

      {files.length > 0 && (
        <>
          <FileListHeader>Files to Combine ({files.length})</FileListHeader>
          <FileList>
            {files.map((file, index) => (
              <FileCard
                key={file.index}
                name={file.name}
                meta={formatSize(file.size)}
                thumb={index + 1}
                actions={
                  <>
                    {/* Hidden, not removed, at the ends of the list so the
                        buttons line up down the column. */}
                    <ListIconButton
                      type="button"
                      aria-label={`Move ${file.name} up`}
                      $hidden={index === 0}
                      disabled={index === 0}
                      onClick={() => moveFile(file.index, 'up')}
                    >
                      <UpChevron>
                        <ChevronDownIcon width="12" height="12" />
                      </UpChevron>
                    </ListIconButton>
                    <ListIconButton
                      type="button"
                      aria-label={`Move ${file.name} down`}
                      $hidden={index === files.length - 1}
                      disabled={index === files.length - 1}
                      onClick={() => moveFile(file.index, 'down')}
                    >
                      <ChevronDownIcon width="12" height="12" />
                    </ListIconButton>
                    <ListIconButton
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeFile(file.index)}
                    >
                      <DeleteIcon
                        strokeColor="currentColor"
                        width="16"
                        height="16"
                      />
                    </ListIconButton>
                  </>
                }
              />
            ))}
          </FileList>
        </>
      )}
    </Container>
  );
});

const Container = styled.div`
  padding: 0;
  background: transparent;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--text-muted);

  &:hover {
    color: var(--gray-400);
  }
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  max-height: 300px;
  overflow-y: auto;
`;

// Ghost icon buttons (docs/design-system.md → Button Hierarchy → Ghost).
const ListIconButton = styled.button<{ $hidden?: boolean }>`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-input);
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  visibility: ${props => (props.$hidden ? 'hidden' : 'visible')};
  transition:
    background-color var(--transition-fast),
    color var(--transition-fast);

  &:hover:not(:disabled) {
    background: var(--border);
    color: var(--text-secondary);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const UpChevron = styled.span`
  display: flex;
  transform: rotate(180deg);
`;

// Same treatment as a dialog field label.
const FileListHeader = styled.h3`
  margin: var(--spacing-lg) 0 var(--spacing-sm);
  padding: 0;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;
